import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import {
  createPlan,
  setPlanActive,
  updatePlan,
} from "../services/plansService";
import type { BrandPlan, NewBrandPlan } from "../types/brand.types";

export interface PlanSave {
  /**
   * True while any of the three is in flight; whichever dialog is open reads
   * it. One flag serves all of them because only one can be open at a time —
   * the form and the activate confirmation are never on screen together.
   */
  saving: boolean;
  /**
   * All three answer with the row the backend stored, or `null` where the call
   * failed — which has already been reported by then, so the caller's only job
   * is to keep the dialog open on a `null` and close it on a row.
   */
  create: (draft: NewBrandPlan) => Promise<BrandPlan | null>;
  update: (
    plan: BrandPlan,
    draft: NewBrandPlan
  ) => Promise<BrandPlan | null>;
  /** Lists a plan on the public page, or takes it off again. */
  setActive: (
    plan: BrandPlan,
    active: boolean
  ) => Promise<BrandPlan | null>;
}

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

/**
 * Creating and editing a plan, with the in-flight flag and the reporting
 * the dialog needs around them.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function usePlanSave(): PlanSave {
  const { getToken } = useAuth();
  const toast = useToast();

  // Clerk hands back a fresh `getToken` on some renders, and this hook sits in
  // a tab that re-renders on every keystroke in its search box — so the calls
  // read the current one through a ref rather than depending on its identity.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const [saving, setSaving] = useState(false);

  // Nothing to abort on unmount: a save that is already with the server should
  // finish. This only stops the result being written back into a gone tab.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /** The shared shape of both: flag up, call, report, flag down. */
  const run = useCallback(
    async (
      call: () => Promise<BrandPlan>,
      failure: string
    ): Promise<BrandPlan | null> => {
      setSaving(true);
      try {
        const saved = await call();
        return saved;
      } catch (error) {
        console.error(`${failure}:`, error);
        toast.error(failure, failureMessage(error));
        return null;
      } finally {
        if (mounted.current) setSaving(false);
      }
    },
    [toast]
  );

  const create = useCallback(
    (draft: NewBrandPlan) =>
      run(
        () => createPlan(() => getTokenRef.current(), draft),
        "Could not add this plan"
      ),
    [run]
  );

  const update = useCallback(
    (plan: BrandPlan, draft: NewBrandPlan) =>
      run(
        () => updatePlan(() => getTokenRef.current(), plan, draft),
        "Could not save this plan"
      ),
    [run]
  );

  const setActive = useCallback(
    (plan: BrandPlan, active: boolean) =>
      run(
        () => setPlanActive(() => getTokenRef.current(), plan.id, active),
        active
          ? "Could not activate this plan"
          : "Could not deactivate this plan"
      ),
    [run]
  );

  return { saving, create, update, setActive };
}

export default usePlanSave;
